<script>
  import t from "$lib/i18n.svelte.js";
  import { fetch_json, fetch_entries, get_visibility_icon } from "$lib/util.js";
  import { invalidateAll } from '$app/navigation';
  import Dialog from "$lib/Dialog.svelte";
  import ButtonLabel from "$lib/ButtonLabel.svelte";
  import ListItem from "$lib/ListItem.svelte";
  import ScrollIndicator from "$lib/ScrollIndicator.svelte";
  import NewCollectionForm from "$lib/NewCollectionForm.svelte";
  import AccordionItem from "$lib/AccordionItem.svelte";

  let { entry, collections, onclose } = $props();

  let collection_previews = $state([]);
  let new_collection_form_is_open = $state();
  let scroll_indicator_ref = $state();
  let last_added_collection_id = $state();

  // We fetch the preview images on demand
  $effect.pre(() => {
    fetch_entries(collections.map(c => c.collection_id), {as_preview: true}).then(entry_previews => {
      collection_previews = entry_previews;
    });
  });

  function scroll_to_bottom() {
    setTimeout(() => {
      scroll_indicator_ref.scrollTo({
        top: scroll_indicator_ref.scrollHeight,
        behavior: 'smooth'
      });
    }, 500);
  }
  
  async function add_to_collection(collection_id) {
    await fetch_json('POST', '/api/add-to-collection', { collection_id, entry_id: entry.entry_id});
    invalidateAll();
    last_added_collection_id = collection_id;
    scroll_to_bottom();
  }

  async function remove_from_collection(collection_id) {
    await fetch_json('POST', '/api/remove-from-collection', { collection_id, entry_id: entry.entry_id});
    invalidateAll();
  }

  async function add_to_newly_created_collection({ collection_id }) {
    await fetch_json('POST', '/api/add-to-collection', { collection_id, entry_id: entry.entry_id});
    invalidateAll();
    last_added_collection_id = collection_id;
    scroll_to_bottom();
  }
</script>
  <Dialog 
    {onclose} 
    class='collection-dialog wide border-1'
  >
    <main>
      <header class='flex-wrap items-start gap-x gap-y g-6 justify-between px py p-6'>
        <div class='flex-column'>
          {#if collections.length === 0}
            <p class='text-body'>{t('no_collections_create_your_first')}</p>
          {:else}
            <p class="text-preamble">{t('save_to_collection')}</p>
            <h3 class="text-heading3"><q>{entry.preview.title}</q></h3>
          {/if}
        </div>
        <button onclick={onclose} title={t('close')}>
          <ButtonLabel variant='brand ghostly' start_icon='close' />
        </button>
      </header>
        
      {#if collections.length !== 0}
        <section>
          <!-- TODO: Don't use max-height here, but instead use a grid with e.g. grid-template-rows: auto 1fr auto; -->
          <ScrollIndicator 
            bind:ref={scroll_indicator_ref}
            class='px p-1 flex-column'
          >
            {#each collections as {collection_id, slug, title, preamble, description, visibility, is_included}, index}
              <ListItem
                id={`collection-${collection_id}`}
                href={slug ? `/${slug}-${collection_id}` : collection_id ? `/${collection_id}` : undefined}
                list_style='image-and-icon'
                images={collection_previews[index]?.images ?? []}
                icon={visibility !== 'public' ? {name: get_visibility_icon(visibility)} : undefined}
                preamble={t(visibility)}
                title={title ?? preamble}
                description={description}
                min_lines={4}
                fit_to_frame={true}
                hover_effect={true}
                class={last_added_collection_id === collection_id ? 'selection-flash-animation' : ''}
              >
                {#snippet tools_end()}
                  {#if is_included}
                    <button onclick={() => { remove_from_collection(collection_id); }} class='me m-4'>
                      <ButtonLabel variant='secondary filled'>{t('remove')}</ButtonLabel>
                    </button>
                  {:else}
                    <button onclick={() => { add_to_collection(collection_id); }} class='me m-4'>
                      <ButtonLabel variant='primary filled'>{t('add')}</ButtonLabel>
                    </button>
                  {/if}
                {/snippet}
              </ListItem>
            {/each}
            <div>&nbsp;</div>
          </ScrollIndicator>
        </section>
      {/if}

      <footer class='flex-column px py p-6'>
        <NewCollectionForm 
          on_collection_created={add_to_newly_created_collection} 
          is_open={collections.length === 0}
        />
      </footer>
    </main>
  </Dialog>

<style lang='postcss'>
  main {
    max-height: inherit;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
      "header"
      "section"
      "footer";
    header {
      grid-area: header;
      border-bottom: 1px solid var(--color-default-stroke);
    }
    section {
      grid-area: section;
      overflow-y: auto;
    }
    footer {
      grid-area: footer;
      border-top: 1px solid var(--color-default-stroke);
    }
  }

  :global(.collection-dialog) {
    max-height: min(var(--dialog-max-height), 600px);
  }
</style>